import { useLocation, useNavigate } from 'react-router-dom'
import { getNavIconUrls } from '../../config/publicIcons.config'
import { logIcon, ScreenPath } from '../../navigation'
import NavIcon from '../icons/NavIcon'
import styles from './TabBar.module.css'

const navUrls = getNavIconUrls()

const SIDE_TABS = [
  { path: ScreenPath.home, label: 'Home', icon: navUrls.homeSecondary, height: 26 },
  { path: ScreenPath.leaderboard, label: 'Milemarker', icon: navUrls.progressSecondary, height: 34 },
] as const

const RIGHT_TABS = [
  { path: ScreenPath.chat, label: 'Chat', icon: navUrls.chatSecondary, height: 24 },
  { path: ScreenPath.team, label: 'Team', icon: navUrls.teamSecondary, height: 24 },
] as const

const HIDE_ON = [ScreenPath.map, ScreenPath.challenges]

export function shouldHideTabBar(pathname: string): boolean {
  return HIDE_ON.some((path) => pathname.startsWith(path))
}

export interface TabBarProps {
  unreadChatCount: number
}

export default function TabBar({ unreadChatCount }: TabBarProps) {
  const navigate = useNavigate()
  const location = useLocation()

  if (shouldHideTabBar(location.pathname)) return null

  function isSelected(path: string) {
    if (path === ScreenPath.home) return location.pathname === path
    return location.pathname === path || location.pathname.startsWith(path)
  }

  return (
    <nav className={styles.bar}>
      {SIDE_TABS.map((item) => (
        <TabButton
          key={item.path}
          label={item.label}
          icon={item.icon}
          height={item.height}
          active={isSelected(item.path)}
          onClick={() => navigate(item.path)}
        />
      ))}

      <button
        type="button"
        className={styles.logTab}
        onClick={() => navigate(ScreenPath.log)}
        aria-current={isSelected(ScreenPath.log) ? 'page' : undefined}
      >
        <div className={styles.logBtn}>
          <NavIcon src={logIcon} height={26} color="currentColor" />
        </div>
      </button>

      {RIGHT_TABS.map((item) => (
        <TabButton
          key={item.path}
          label={item.label}
          icon={item.icon}
          height={item.height}
          active={isSelected(item.path)}
          badge={item.path === ScreenPath.chat ? unreadChatCount : 0}
          onClick={() => navigate(item.path)}
        />
      ))}
    </nav>
  )
}

function TabButton({
  label,
  icon,
  height,
  active,
  badge = 0,
  onClick,
}: {
  label: string
  icon: string
  height: number
  active: boolean
  badge?: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={[styles.tab, active && styles.active, badge > 0 && styles.relative]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
    >
      <span className={styles.navIcon}>
        <NavIcon src={icon} height={height} color="currentColor" />
      </span>
      <span className={styles.label}>{label}</span>
      {badge > 0 && <span className={styles.badge}>{badge}</span>}
    </button>
  )
}
